import {
    Button,
    ButtonText,
    FormField,
    HStack,
    Input,
    InputField,
    SelectGeneric,
    useNumberInputRules,
    VStack
} from "@hosttools/frontend";
import type React from "react";
import {memo} from "react";
import {Controller, useController, useForm} from "react-hook-form";

import {Card, CardBody, CardFooter} from "../Card";

import {feeTypes, taxTypes, units, valueTypes} from "./constant";
import type {Fee} from "./types";

type Props = {
    fee: Fee | undefined;
    currency: string;
    onSave: (item: Fee) => void;
    onCancel: () => void;
};

const FeeForm: React.FC<Props> = ({fee, currency, onSave, onCancel}) => {
    const {
        control,
        watch,
        handleSubmit,
        formState: {errors}
    } = useForm<Fee>({
        defaultValues: fee ?? {valueType: "FLAT", entityType: "MANDATORY"}
    });
    const {
        field: {value: valueType}
    } = useController({control, name: "valueType"});

    // not sure if we should unsubscribe manually?
    const unit = watch("unit");
    const inputNumberRules = useNumberInputRules();

    return (
        <Card>
            <CardBody>
                <VStack space="lg">
                    <VStack className="lg:flex-row" space="lg">
                        <FormField error={errors?.name?.message} label="Fee name">
                            <Controller
                                control={control}
                                name="name"
                                render={({field}) => {
                                    return (
                                        <Input isRequired>
                                            <InputField
                                                id="name"
                                                placeholder="Fee name..."
                                                testID="name"
                                                type="text"
                                                value={field.value}
                                                onBlur={field.onBlur}
                                                onChange={field.onChange}
                                            />
                                        </Input>
                                    );
                                }}
                                rules={{
                                    required: "This field is required"
                                }}
                            />
                        </FormField>
                        <FormField error={errors?.feeType?.message} label="Fee type">
                            <Controller
                                control={control}
                                name="feeType"
                                render={({field}) => {
                                    return (
                                        <SelectGeneric
                                            options={feeTypes}
                                            placeholder="Select fee type..."
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    );
                                }}
                                rules={{required: "This field is required"}}
                            />
                        </FormField>
                        <FormField error={errors?.taxType?.message} label="Tax type">
                            <Controller
                                control={control}
                                name="taxType"
                                render={({field}) => {
                                    return (
                                        <SelectGeneric
                                            options={taxTypes}
                                            placeholder="Select tax type..."
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    );
                                }}
                                rules={{required: "This field is required"}}
                            />
                        </FormField>
                    </VStack>

                    <VStack className="lg:flex-row" space="lg">
                        <FormField error={errors?.unit?.message} label="Unit">
                            <Controller
                                control={control}
                                name="unit"
                                render={({field}) => {
                                    return (
                                        <SelectGeneric
                                            options={units}
                                            placeholder="Select unit..."
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    );
                                }}
                                rules={{required: "This field is required"}}
                            />
                        </FormField>
                        <FormField error={errors?.valueType?.message} label="Value type">
                            <Controller
                                control={control}
                                name="valueType"
                                render={({field}) => {
                                    return (
                                        <SelectGeneric
                                            options={valueTypes}
                                            placeholder="Select value type..."
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    );
                                }}
                                rules={{required: "This field is required"}}
                            />
                        </FormField>
                        <FormField
                            error={errors.value?.message}
                            label={valueType === "FLAT" ? "Amount" : "Percentage"}
                        >
                            <Controller
                                control={control}
                                name="value"
                                render={({field}) => {
                                    return (
                                        <Input
                                            isRequired
                                            leftElement={
                                                valueType === "FLAT" ? currency : undefined
                                            }
                                            rightElement={valueType === "PERCENT" ? "%" : undefined}
                                        >
                                            <InputField
                                                id="value"
                                                placeholder={
                                                    valueType === "FLAT"
                                                        ? "Amount..."
                                                        : "Percentage..."
                                                }
                                                testID="value"
                                                type="number"
                                                value={field.value ?? ""}
                                                onBlur={field.onBlur}
                                                onChange={field.onChange}
                                            />
                                        </Input>
                                    );
                                }}
                                rules={{
                                    required: "This field is required",
                                    ...inputNumberRules
                                }}
                            />
                        </FormField>
                        {["PER_PERSON", "PER_DAY_PER_PERSON_EXTRA"].includes(unit) && (
                            <FormField error={errors.option?.message} label="Guests without fee">
                                <Controller
                                    control={control}
                                    name="option"
                                    render={({field}) => {
                                        return (
                                            <Input isRequired>
                                                <InputField
                                                    placeholder="Guests without fee..."
                                                    testID="option"
                                                    type="number"
                                                    value={field.value ?? ""}
                                                    onBlur={field.onBlur}
                                                    onChange={field.onChange}
                                                />
                                            </Input>
                                        );
                                    }}
                                    rules={{
                                        required: "This field is required",
                                        ...inputNumberRules
                                    }}
                                />
                            </FormField>
                        )}
                    </VStack>
                </VStack>
            </CardBody>
            <CardFooter>
                <HStack className="justify-end" space="sm">
                    <Button variant="ghost" onPress={onCancel}>
                        <ButtonText>Cancel</ButtonText>
                    </Button>
                    <Button onPress={handleSubmit(onSave)}>
                        <ButtonText>Save</ButtonText>
                    </Button>
                </HStack>
            </CardFooter>
        </Card>
    );
};

export default memo(FeeForm);
